import { FormHandles } from '@unform/core';
import { Form } from '@unform/web';
import { Select } from 'components/Shared';
import type { SelectOption } from 'contracts/Common';
import {
  DeleteCompanyVehicleTypeActions as DeleteActions,
  CreateCompanyVehicleTypeActions as CreateActions,
} from 'store/ducks/companyVehicleTypes';
import { useValidation } from 'hooks';
import {
  forwardRef,
  ForwardRefExoticComponent,
  RefAttributes,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { AppDispatch, RootState } from 'store';
import * as Yup from 'yup';
import * as S from './styles';
import { CompanyVehicleType } from 'contracts/Companies';

export interface Ref {
  resetForm: () => void;
}

interface Props {
  onUpdate?: () => void;
  companyId: string;
}

interface IVehicleTypesManager
  extends ForwardRefExoticComponent<Props & RefAttributes<Ref>> {}

interface ItemProps {
  vehicleType: CompanyVehicleType['vehicleType'];
  id: number;
}

const VehicleTypesManager: IVehicleTypesManager = forwardRef<Ref, Props>(
  ({ companyId, onUpdate }, ref) => {
    const { data: company } = useSelector(
      (state: RootState) => state.fetchCompany
    );

    const dispatch: AppDispatch = useDispatch();
    const formRef = useRef<FormHandles>(null);

    const { handleFormErrors, handleApiErrors } = useValidation();

    /**
     * this component will not fetch vehicleTypes here,
     * we lifted it to a upper level (DocksManager component)
     * to avoid dispatching multiple times
     * */
    const { loading: loadingVehicleTypes, data: vehicleTypes } = useSelector(
      (state: RootState) => state.listVehicleTypes
    );

    const { loading: creating, validationErrors } = useSelector(
      (state: RootState) => state.createCompanyVehicleType
    );

    const { id: deletingId, loading: deleting } = useSelector(
      (state: RootState) => state.deleteCompanyVehicleType
    );

    /**
     * we will also filter the list of vehicleTypes to only show
     * the ones that are not already bound to the dock
     * */

    const vehicleTypeOptions: SelectOption[] | undefined = useMemo(() => {
      if (!vehicleTypes?.length) return undefined;

      return vehicleTypes
        .filter((vehicleType) =>
          company?.companyVehicleTypes
            ? !company.companyVehicleTypes.find(
                (companyVehicleType) =>
                  companyVehicleType.vehicleType.id === vehicleType.id
              )
            : true
        )
        .map((vehicleType) => ({
          value: vehicleType.id,
          label: vehicleType.name,
        }));
    }, [vehicleTypes, company]);

    const onDelete = useCallback(
      (id?: number) => dispatch(DeleteActions.request(id, onUpdate)),
      [dispatch, onUpdate]
    );

    const onSuccess = useCallback((): void => {
      onUpdate && onUpdate();
      formRef.current?.reset();
    }, [onUpdate]);

    const onSubmit = useCallback(
      async (data: any): Promise<void> => {
        try {
          formRef.current?.setErrors({});
          const schema = Yup.object().shape({
            vehicleTypeId: Yup.number()
              .typeError('Selecione o cliente')
              .required('Selecione o cliente'),
          });
          const validData = await schema.validate(data, {
            abortEarly: false,
          });
          dispatch(
            CreateActions.request({ ...validData, companyId }, onSuccess)
          );
        } catch (error) {
          handleFormErrors(error, formRef);
        }
      },
      [dispatch, handleFormErrors, onSuccess, companyId]
    );

    const Item = useCallback(
      ({ vehicleType, id }: ItemProps): JSX.Element => {
        const { id: vehicleTypeId, name } = vehicleType;

        return (
          <S.ListItem>
            <S.Column>{vehicleTypeId}</S.Column>
            <S.Column>{name}</S.Column>
            <S.ActionsColumn>
              <S.ActionButton
                disabled={deletingId === vehicleTypeId || deleting}
                onClick={() => onDelete(id)}
                mood="danger"
              >
                {deletingId === vehicleTypeId ? (
                  <S.ActivityIndicator />
                ) : (
                  <S.TrashIcon />
                )}
              </S.ActionButton>
            </S.ActionsColumn>
          </S.ListItem>
        );
      },
      [deletingId, onDelete, deleting]
    );

    useImperativeHandle(
      ref,
      () => ({
        resetForm: () => {
          formRef.current?.reset();
          formRef.current?.setErrors({});
        },
      }),
      []
    );

    useEffect(() => {
      handleApiErrors(validationErrors, formRef);
    }, [handleApiErrors, validationErrors]);

    return (
      <S.Container>
        <Form ref={formRef} onSubmit={onSubmit}>
          <S.FormRow>
            <Select
              name="vehicleTypeId"
              label="Tipo de veículo"
              options={vehicleTypeOptions}
              isLoading={loadingVehicleTypes}
              menuPortalTarget={document.body}
              menuPlacement="top"
            />
            <S.Button type="submit" disabled={creating}>
              {creating ? <S.ActivityIndicator /> : 'Adicionar'}
            </S.Button>
          </S.FormRow>
        </Form>
        {!company?.companyVehicleTypes?.length ? (
          <S.EmptyListPlaceholder>
            Esse armazém ainda não possui restrições para tipos de veículos
          </S.EmptyListPlaceholder>
        ) : (
          <>
            <S.ListHeader>
              <div>ID</div>
              <div>NOME</div>
            </S.ListHeader>
            {company?.companyVehicleTypes?.map((props) => (
              <Item {...props} key={props.id} />
            ))}
          </>
        )}
      </S.Container>
    );
  }
);

export default VehicleTypesManager;
